import { EventBus, Events } from '../../managers/EventBus';
import { StateManager } from '../../managers/StateManager';
import { APIManager } from '../../managers/APIManager';
import type { UnitData, PlayerData, NotificationData } from '../../types';

class NavBarClass {
  private el: HTMLElement | null = null;
  private tabs: NodeListOf<HTMLElement> | null = null;

  /** Tracks which tabs the player has visited this session (for craft/loom one-time badges). */
  private visitedTabs = new Set<string>();

  initialize(): void {
    this.el = document.getElementById('nav-bar');
    if (!this.el) return;

    this.tabs = this.el.querySelectorAll('.nav-tab');

    // Track tab visits from clicks
    this.el.addEventListener('click', (e) => {
      const tab = (e.target as HTMLElement).closest('.nav-tab') as HTMLElement;
      if (!tab) return;
      const screenId = tab.dataset.screen;
      if (screenId) {
        this.visitedTabs.add(screenId);

        // Clear server-side notification flags when visiting party
        if (screenId === 'party') {
          APIManager.clearNotification('party').then(result => {
            if (result.success && result.data) {
              StateManager.set('notificationData', result.data as NotificationData);
            }
          });
        }

        this.updateBadges();
      }
    });

    // Also track programmatic navigation
    EventBus.on(Events.NAVIGATE, (id: unknown) => {
      const screenId = id as string;
      this.visitedTabs.add(screenId);
      if (screenId === 'party') {
        APIManager.clearNotification('party').then(result => {
          if (result.success && result.data) {
            StateManager.set('notificationData', result.data as NotificationData);
          }
        });
      }
      this.updateBadges();
    });

    // Recompute badge state when relevant state changes
    StateManager.subscribe('notificationData', () => this.updateBadges());
    StateManager.subscribe('unitData', () => this.updateBadges());
    StateManager.subscribe('playerData', () => this.updateBadges());

    // Initial badge compute
    this.updateBadges();
  }

  setActive(screenId: string): void {
    this.tabs?.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.screen === screenId);
    });
  }

  show(): void { this.el?.classList.remove('hidden'); }
  hide(): void { this.el?.classList.add('hidden'); }

  /** Compute and display notification badges on nav tabs. */
  private updateBadges(): void {
    if (!this.el) return;

    const notif = StateManager.get('notificationData') as NotificationData | null;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const player = StateManager.get('playerData') as PlayerData | undefined;

    // PARTY badge: server says there are new items, strands, or strand level-ups
    const hasStrandAlerts = notif?.strandAlerts && Object.keys(notif.strandAlerts).length > 0;
    const partyBadge = !!(notif?.newItems || notif?.newStrands || hasStrandAlerts);

    // CRAFT badge: player has at least 1 vivid spool (cheapest craft operation)
    const craftBadge = !this.visitedTabs.has('crafting') && (player?.vividSpools ?? 0) >= 1;

    // LOOM badge: player has 2+ units of any class AND at least 1 soulthread
    let loomBadge = false;
    if (!this.visitedTabs.has('soulloom') && (player?.soulthread ?? 0) >= 1 && unitData?.roster) {
      const classCounts: Record<string, number> = {};
      for (const unit of unitData.roster) {
        classCounts[unit.unitClass] = (classCounts[unit.unitClass] ?? 0) + 1;
        if (classCounts[unit.unitClass] >= 2) { loomBadge = true; break; }
      }
    }

    // Set badge visibility per tab
    this.setBadge('party', partyBadge);
    this.setBadge('crafting', craftBadge);
    this.setBadge('soulloom', loomBadge);

    // Campaign/replay tab is hidden until Ch1 is finished — replays only exist once
    // the player has cleared at least one chapter. Appears on completion of Ch1.
    const campaignUnlocked = (player?.campaignStep ?? 0) >= 1;
    this.setTabVisibility('crawls', campaignUnlocked);
  }

  private setBadge(screenId: string, visible: boolean): void {
    const tab = this.el?.querySelector(`.nav-tab[data-screen="${screenId}"]`);
    if (!tab) return;
    const badge = tab.querySelector('.nav-badge');
    if (badge) {
      badge.classList.toggle('hidden', !visible);
    }
  }

  private setTabVisibility(screenId: string, visible: boolean): void {
    const tab = this.el?.querySelector(`.nav-tab[data-screen="${screenId}"]`);
    if (!tab) return;
    (tab as HTMLElement).classList.toggle('hidden', !visible);
  }
}

export const NavBar = new NavBarClass();
